<script setup lang="ts">
import {
  IconLanguage,
  IconSortAscending2,
  IconSortDescending2,
} from "@tabler/icons-vue";
import type { AvailableLanguages } from "~/src/api";
import type { Option } from "~/types/input";

const sortDesc = defineModel<boolean>("sortDesc", {
  required: true,
  default: false,
});
const translationLocale = defineModel<AvailableLanguages>("translationLocale", {
  required: true,
});

const { t: translate } = useNuxtApp().$i18n.global;

defineProps<{
  loading?: boolean;
  allTranslationLocales: Option<AvailableLanguages>[];
}>();
</script>

<template>
  <div class="flex justify-between space-x-2 mb-4 lg:-mt-2">
    <NamiButton
      button-type="secondary"
      text
      @buttonClick="sortDesc = !sortDesc"
      :skeleton="loading"
      :disabled="loading"
    >
      <div class="flex items-center space-x-2 whitespace-nowrap">
        <IconSortDescending2 v-if="sortDesc" />
        <IconSortAscending2 v-else />
        <span>{{
          sortDesc
            ? translate("site.title.id.slug.chapters.optionDescending")
            : translate("site.title.id.slug.chapters.optionAscending")
        }}</span>
      </div>
    </NamiButton>
    <ContextMenu close-on-outside-click>
      <template #facing="{ isOpen, open, close }">
        <NamiButton
          button-type="secondary"
          text
          :skeleton="loading"
          :disabled="loading"
          @click.stop="() => (isOpen ? close() : open())"
        >
          <div class="flex items-center space-x-2 whitespace-nowrap">
            <IconLanguage />
            <span>{{
              allTranslationLocales.find((e) => e.value === translationLocale)
                ?.text
            }}</span>
          </div>
        </NamiButton>
      </template>
      <template #menu="{ close }">
        <ul
          class="min-w-max divide-y divide-neutral-300/50 dark:divide-neutral-700/50"
        >
          <li
            v-for="option in allTranslationLocales"
            class="relative cursor-pointer"
            ref="optionItems"
          >
            <div
              class="flex items-center px-4 py-2 select-none transition-colors duration-75 hover:bg-neutral-200/50 hover:dark:bg-neutral-600/50"
              :class="{ 'font-bold': translationLocale === option.value }"
              @pointerup="
                () => {
                  translationLocale = option.value;
                  close();
                }
              "
            >
              {{ option.text }}
            </div>
          </li>
        </ul>
      </template>
    </ContextMenu>
  </div>
</template>
